import * as Mui from "@mui/material";
import { Link, Route, Routes, useParams, useSearchParams } from "react-router-dom";
import { useLocation } from "react-router-dom";
import GeneralInfo from "./generalInfo/GeneralInfo";
import Messages from "./messages/Messages";
import Task1 from "./tasks/task1/Task1";
import Task2 from "./tasks/task2/Task2";
import Task3 from "./tasks/task3/Task3";
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DamageFormService from "./service/DamageFormService";
import { CSSProperties, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";
import { customTheme } from "../../../../config/theme/MuiTheme";
import Dialogs from "./tasks/dialogs";
import useToggle from "../../hooks/useToggle";
import useSnackbar from "../../hooks/useSnackbar";
import { ContextOperation } from "../../context/OperationContext";
import { DamageFormTypes } from "./Types";
import useOperationTranslation from "../../hooks/useOperationTranslation";
import Actions from "./actions/Actions";
import { ContextDamageForm } from "./DamageFormContext";
import { RecoilRoot } from "recoil";

interface ITabLabelProps extends PropsWithChildren<any> {
  title: string;
  color?: CSSProperties["color"];
}

const TabLabel = (props: ITabLabelProps) => {

  const extraProps = {
    display: "flex",
    alignItems: "center",
    ...props.color && ({
      color: props.color
    }),

  };

  return (
    <Mui.Typography variant="h6" {...extraProps}>
      {props.title}
      {props.children}
    </Mui.Typography>
  )
};

const DamageFormNavigator = () => {
  const [searchParams] = useSearchParams();
  const operationContext = useContext(ContextOperation);
  const tenant = searchParams.get("tenant") ?? operationContext.tenant;

  const t = useOperationTranslation();

  const location = useLocation();
  const snackBar = useSnackbar();

  const { id } = useParams();
  
  const [openTask2Confirm, toggleTask2Confirm] = useToggle();
  const [openTask3Confirm, toggleTask3Confirm] = useToggle();
  const damageFormContext = useContext(ContextDamageForm);
  
  const[tasksPresent, setTasksPresent ] = useState<DamageFormTypes.ITaskExist>();

  useEffect(() => {
    if (!id) return;
    DamageFormService.details.get.taskExist(id, tenant).then(
      (response) => {
        setTasksPresent(response)       
      }
    )
  }, []);

  useEffect(() => {
    const searchTenant = searchParams.get("tenant");
    if (searchTenant && searchTenant !== operationContext.tenant) {
      operationContext.setTenant(searchTenant);
    }
  }, [tenant]);

  const onOpenTask2 = () => {
    if (!id) return;
    DamageFormService.details.task2.post.openById(id).then(
      (response) => {
        DamageFormService.details.get.taskExist(id, tenant).then(
          (response) => {
            setTasksPresent(response)       
          }
        );
        toggleTask2Confirm();
        snackBar.show("success", t("damageForm.details.tabs.snackbar.task2opened.successfull"));
        },
        (error)=>{
          snackBar.show("error", t("damageForm.details.tabs.snackbar.task2opened.error"));
      }
    )
  }

  const onOpenTask3 = () => {
    if (!id) return;
    DamageFormService.details.task3.post.openById(id).then(
      (response) => {
        DamageFormService.details.get.taskExist(id, tenant).then(
          (response) => {
            setTasksPresent(response)       
          }
        );
        toggleTask3Confirm();
        snackBar.show("success", t("damageForm.details.tabs.snackbar.task3opened.successfull"));
        },
        (error)=> {
          snackBar.show("error", t("damageForm.details.tabs.snackbar.task3opened.error"));
        }
    )
  }

  const basePath = useMemo(() => `/operation/damages/forms/details/${id}`, []);
  return(
    <>
      <Mui.Box sx={{ padding: "0 16px" }}>
        <Mui.Tabs value={location.pathname}>
          <Mui.Tab
            to={`${basePath}?tenant=${tenant}`}
            component={Link}
            label={<TabLabel title={t("damageForm.details.tabs.generalInfo")} />}
            value={basePath}
          />

          <Mui.Tab
            to={`${basePath}/task1?tenant=${tenant}`}
            component={Link}
            label={<TabLabel title={t("damageForm.details.tabs.task1")} />}
            value={`${basePath}/task1`}
          />
          
          <Mui.Tab
            to={ tasksPresent?.taskTwoIsPresent ? `${basePath}/task2?tenant=${tenant}` : ""}
            component={Link}
            onClick={() => !tasksPresent?.taskTwoIsPresent && !damageFormContext.generalInfo?.migrated && toggleTask2Confirm() }
            label={
              <TabLabel
                title={t("damageForm.details.tabs.task2")}
                color={!tasksPresent?.taskTwoIsPresent === true ? customTheme.palette.neutral.dark : undefined}
              >
                {!tasksPresent?.taskTwoIsPresent && (
                    <AddCircleOutlineIcon sx={{ml: "5px"}} />
                )}
              </TabLabel>
            }
            value={`${basePath}/task2`}
          />
          
          <Mui.Tab
            to={ tasksPresent?.taskThreeIsPresent ?  `${basePath}/task3?tenant=${tenant}` : ""}
            component={Link}
            onClick={() => !tasksPresent?.taskThreeIsPresent && !damageFormContext.generalInfo?.migrated && toggleTask3Confirm() }            
            label={
              <TabLabel
                title={t("damageForm.details.tabs.task3")}
                color={!tasksPresent?.taskThreeIsPresent === true ? customTheme.palette.neutral.dark : undefined}
              >
                {!tasksPresent?.taskThreeIsPresent && (
                  <AddCircleOutlineIcon sx={{ml: "5px"}} />                           
                )}
              </TabLabel>
            }
            value={`${basePath}/task3`}
          />

          <Mui.Tab
            to={`${basePath}/messages?tenant=${tenant}`}
            component={Link}
            label={<TabLabel title={t("damageForm.details.tabs.messages")} />}
            value={`${basePath}/messages`}
          />
          
          <Mui.Tab
            to={`${basePath}/actions?tenant=${tenant}`}
            component={Link}
            label={<TabLabel title={t("damageForm.details.tabs.actions")} />}
            value={`${basePath}/actions`}
          />
        </Mui.Tabs>

        <RecoilRoot>
        <Routes>
          <Route path="/" element={<GeneralInfo />} />
          <Route path="/task1" element={<Task1 />} />
          <Route path="/task2" element={<Task2 />} />
          <Route path="/task3" element={<Task3 />} />
          <Route path="/messages" element={<Messages />} />
          <Route path="/actions" element={<Actions />} />
        </Routes>
        </RecoilRoot>
      </Mui.Box>

      <Dialogs.OpenTaskConfirmation
        open={openTask2Confirm}
        onClose={() => toggleTask2Confirm()}
        taskName="task 2"
        onOpenTask={() =>onOpenTask2()}
      />


      <Dialogs.OpenTaskConfirmation 
        open={openTask3Confirm} 
        onClose={() => toggleTask3Confirm()}
        taskName="task 3"
        onOpenTask={() =>onOpenTask3()}
      />
          
    </>
  );
};
export default DamageFormNavigator;